/**
 * QultureWeeklyChart
 *
 * The 7-day "This Week" engagement bar chart for QultureUp / Adaptation —
 * promoted to @tonnie/ui so the citizen QultureUp Overview tab and the business
 * Sponsored Adaptation drawer render the SAME chart from the SAME code.
 *
 * Presentational + self-contained: pass the per-day mission counts (oldest →
 * newest) and it renders the bars, the value above each bar, the weekday labels,
 * and the "today" highlight. The caller wraps it in their own Card + header.
 */

import { cn } from '@tonnie/foundation';
import { HERO_COLORS } from '@tonnie/design-tokens';

export interface QultureWeeklyChartProps {
  /** Per-day missions completed for the current ISO week (Mon → Sun). */
  weekly: number[];
  /**
   * The APP locale for the weekday labels — `useLocale().intlLocale`. Required:
   * the optional form defaulted to the BROWSER's locale, and no caller passed it,
   * so a pt-PT reader got weekday names in whatever their browser was set to (F34).
   */
  locale: string;
  className?: string;
  ariaLabel?: string;
}

/** Monday-anchored weekday labels for the current week. */
function weekdayLabels(locale: string): string[] {
  const labels: string[] = [];
  const now = new Date();
  const dow = now.getDay();
  const mondayOffset = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(now);
  monday.setDate(monday.getDate() + mondayOffset);
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    labels.push(d.toLocaleDateString(locale, { weekday: 'short' }));
  }
  return labels;
}

export function QultureWeeklyChart({
  weekly,
  locale,
  className,
  ariaLabel,
}: QultureWeeklyChartProps) {
  const colors = HERO_COLORS.qultureup;
  const days = weekdayLabels(locale);
  const maxActivity = Math.max(...weekly, 1);
  // Monday-anchored index for "today" (getDay(): Sun=0 → 6, Mon=1 → 0, …).
  const todayIndex = (new Date().getDay() + 6) % 7;

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Definite track height — a percentage bar height only resolves against
          a parent with a DEFINITE height. `flex-1`/`min-h` are indefinite, so
          the bars collapsed to 0 in auto-height containers (e.g. the business
          Adaptation drawer card) while still rendering in flex-stretched ones
          (the citizen page). A fixed track height makes the chart render
          correctly regardless of the consumer's layout. */}
      <div className="flex items-end gap-2 h-40" role="img" aria-label={ariaLabel}>
        {days.map((day, i) => {
          const val = weekly[i] ?? 0;
          const pct = Math.max((val / maxActivity) * 100, 3);
          const isToday = i === todayIndex;
          return (
            <div key={day} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
              {val > 0 && (
                <span
                  className="text-2xs font-semibold tabular-nums mb-1"
                  style={{ color: colors.text }}
                >
                  {val}
                </span>
              )}
              <div
                className={cn(
                  'w-full rounded-t-sm transition-[height] duration-500 ease-out',
                  isToday && 'ring-1 ring-offset-1 ring-offset-background'
                )}
                style={
                  {
                    height: `${pct}%`,
                    backgroundColor: val > 0 ? colors.primary : 'var(--muted)',
                    opacity: val > 0 ? 1 : 0.3,
                    '--tw-ring-color': isToday ? colors.primary : undefined,
                  } as React.CSSProperties
                }
              />
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2">
        {days.map((day, i) => (
          <span
            key={day}
            className={cn(
              'flex-1 text-center text-2xs',
              i === todayIndex ? 'font-semibold text-foreground' : 'text-muted-foreground'
            )}
          >
            {day}
          </span>
        ))}
      </div>
    </div>
  );
}
